'use client';

import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Calendar, User, BookOpen, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { getAllPosts } from '@/lib/blog';
import { PageLayout } from '@/components/PageLayout';
import { Meta } from '@/components/MetaManager';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';

export default function Blog() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const language = i18n.language as 'en' | 'zh';
  const posts = getAllPosts(language);

  const metaTitle = language === 'zh' ? '博客 | borninsea' : 'Blog | borninsea';
  const metaDesc = language === 'zh'
    ? '分享我在创伤疗愈、AI 创业和产品开发路上的思考与实践'
    : 'Sharing thoughts and practices on trauma healing, AI entrepreneurship, and product development';
  const metaKeywords = language === 'zh'
    ? '博客,创伤疗愈,AI创业,产品开发,个人成长'
    : 'blog,trauma healing,AI entrepreneurship,product development,personal growth';

  const breadcrumbs = generateBreadcrumbs([
    { label: language === 'zh' ? '博客' : 'Blog', path: '/blog' }
  ], language);

  return (
    <PageLayout>
      <Meta title={metaTitle} description={metaDesc} keywords={metaKeywords} />
      <StructuredData type="website" breadcrumbs={breadcrumbs} pageTitle={metaTitle} pageDescription={metaDesc} />
      <div className="relative min-h-[40vh] bg-black overflow-hidden">
        {/* 背景渐变 */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent-purple/15 via-transparent to-accent-blue/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent-emerald/5 via-transparent to-transparent" />
        
        {/* 装饰性光效 */}
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-accent-purple/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 left-1/4 w-64 h-64 bg-accent-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* 内容 */}
        <div className="relative z-10 container mx-auto px-6 pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 glass-effect px-4 py-2 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-accent-purple" />
              <span className="text-sm font-medium text-white/80">Blog</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white leading-tight">
              {t.nav.blog}
            </h1>
            
            <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto">
              {i18n.language === 'zh' 
                ? t.common?.blogIntro || '分享我在创伤疗愈、AI 创业和产品开发路上的思考与实践'
                : t.common?.blogIntroEn || 'Sharing thoughts and practices on trauma healing, AI entrepreneurship, and product development'
              }
            </p>
            
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="glass-effect px-4 py-2 rounded-lg">
                <span className="text-2xl font-bold text-white">{posts.length}</span>
                <span className="text-white/60 ml-2 text-sm">{i18n.language === 'zh' ? '篇文章' : 'Posts'}</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Blog Posts Grid */}
      <div className="bg-background py-16">
        <div className="container mx-auto px-6">
          {posts.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="glass-effect inline-block p-8 rounded-2xl">
                <Sparkles className="w-8 h-8 text-accent-purple mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {t.common.noBlogPosts || (i18n.language === 'zh' ? '暂无博客文章' : 'No blog posts yet')}
                </p>
              </div>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={getLocalizedPath(`/blog/${post.slug}`)}>
                    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      {/* Cover Image */}
                      {post.coverImage && (
                        <div className="aspect-video bg-muted overflow-hidden">
                          <img 
                            src={post.coverImage} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="p-6">
                        {/* Tags */}
                        {post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {post.tags.slice(0, 3).map(tag => (
                              <span 
                                key={tag}
                                className="text-xs px-2.5 py-1 bg-accent-purple/10 text-accent-purple rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Title */}
                        <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>

                        {/* Description */}
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                          {post.description}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-border">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(post.date).toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US')}
                          </span>
                          {post.author && (
                            <span className="flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5" />
                              {post.author}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
